import type { FieldProps } from "./types";
import { TextField, TextArea, SegmentedControl, Select } from "@upstart.gg/style-system/system";
import { TbSlash } from "react-icons/tb";
import { useDebounceCallback } from "usehooks-ts";
import { FieldTitle } from "../field-factory";
import { tx } from "@upstart.gg/style-system/twind";
import type { UrlOrPageIdSettings } from "@upstart.gg/sdk/shared/bricks/props/string";
import { type ChangeEvent, type FC, useRef, useState } from "react";
import { useDraftHelpers, usePagePathParams, usePageQueries, useSitemap } from "~/editor/hooks/use-page-data";
import { useDynamicTextEditor } from "~/editor/hooks/use-editable-text";

export const StringField: FC<FieldProps<string>> = (props) => {
  const { currentValue, onChange, title, description, placeholder, schema, brickId, noDynamic } = props;
  const pageQueries = usePageQueries();
  const onChangeDebounced = useDebounceCallback(onChange, 300);
  const DynamicTextEditor = useDynamicTextEditor(props);

  if (pageQueries.length && !noDynamic && !schema["ui:no-dynamic"]) {
    return (
      <div className="field field-string basis-full flex flex-col gap-1 max-w-full">
        <FieldTitle title={title} description={description} />
        <div className="field field-string flex items-start gap-1.5">{DynamicTextEditor}</div>
      </div>
    );
  }

  return (
    <div className="field field-string basis-full">
      <FieldTitle title={title} description={description} />
      {schema["ui:multiline"] ? (
        <TextArea
          defaultValue={currentValue}
          onChange={(e) => onChangeDebounced(e.target.value)}
          className={tx("!mt-1.5 scrollbar-thin p-1", schema["ui:textarea-class"] ?? "min-h-24")}
          placeholder={placeholder}
          size={schema["ui:textarea-font-size"] ?? "2"}
          spellCheck={!!schema["ui:spellcheck"]}
        />
      ) : (
        <TextField.Root
          defaultValue={currentValue}
          onChange={(e) => onChangeDebounced(e.target.value)}
          className="!mt-1"
          size={"2"}
          placeholder={placeholder}
          spellCheck={!!schema["ui:spellcheck"]}
        />
      )}
    </div>
  );
};

export const PathField: FC<FieldProps<string>> = (props) => {
  const { currentValue, onChange, title, description, placeholder } = props;
  const onChangeDebounced = useDebounceCallback(onChange, 300);
  // remove leading slash
  const path = (currentValue || "").toString().replace(/^\//, "");
  const params = usePagePathParams();

  return (
    <div className="field field-path basis-full">
      <FieldTitle withIcon title={title} description={description} />
      <TextField.Root
        defaultValue={path}
        onChange={(e) => onChangeDebounced(e.target.value)}
        className="!mt-1"
        placeholder={placeholder}
      >
        <TextField.Slot>
          <TbSlash className="bg-transparent h-5 w-5 rounded-md stroke-1 !-ml-1 !-mr-2 -rotate-[20deg]" />
        </TextField.Slot>
      </TextField.Root>
      {params.length > 0 && (
        <div className="mt-1.5 text-xs text-gray-500">
          <span>You'll be able to use the variable{params.length > 1 ? "s" : ""} </span>{" "}
          {params.map((param, index) => (
            <span key={param} className="font-medium">
              {param}
              {index === params.length - 2 ? " and " : index < params.length - 1 ? ", " : ""}{" "}
            </span>
          ))}
          in queries.
        </div>
      )}
    </div>
  );
};

export const UrlOrPageIdField: FC<FieldProps<UrlOrPageIdSettings | null>> = (props) => {
  const { currentValue, onChange, title, description, placeholder, schema, brickId } = props;
  const sitemap = useSitemap();
  const [type, setType] = useState<"url" | "pageId">(
    currentValue?.startsWith("http") || sitemap.length === 1 ? "url" : "pageId",
  );
  const pageQueries = usePageQueries();
  const externalLabel = pageQueries.length ? "External / Dynamic" : "External link";
  const DynamicTextEditor = useDynamicTextEditor(props);

  return (
    <div className="flex-1 max-w-full">
      <div className="flex justify-between flex-1 gap-1">
        <FieldTitle title={title} description={description} />
        <SegmentedControl.Root
          onValueChange={(value) => {
            setType(value as "url" | "pageId");
            // reset current value if switching of type
            onChange(null);
          }}
          defaultValue={type}
          size="1"
          className="mt-0.5"
          radius="medium"
        >
          <SegmentedControl.Item value="url" className="[&_.rt-SegmentedControlItemLabel]:!px-2.5">
            {externalLabel}
          </SegmentedControl.Item>
          <SegmentedControl.Item value="pageId" className="[&_.rt-SegmentedControlItemLabel]:!px-0">
            Internal
          </SegmentedControl.Item>
        </SegmentedControl.Root>
      </div>
      {type === "url" ? (
        pageQueries.length > 0 ? (
          <div className="field field-string basis-full flex items-start gap-1.5 mt-2">
            {DynamicTextEditor}
          </div>
        ) : (
          <TextField.Root
            defaultValue={currentValue?.startsWith("http") ? currentValue : ""}
            onChange={(e) => onChange(e.target.value)}
            className="!mt-2 max-w-full"
            placeholder="https://example.com"
            spellCheck={!!schema["ui:spellcheck"]}
          />
        )
      ) : (
        <Select.Root
          defaultValue={currentValue ?? undefined}
          size="2"
          onValueChange={(value) => onChange(value)}
        >
          <Select.Trigger
            radius="large"
            variant="surface"
            className="!mt-2 !w-full !flex-1 !max-w-full truncate"
            placeholder="Select a page"
          />
          <Select.Content position="popper">
            <Select.Group>
              {Object.entries(sitemap).map(([, page]) => (
                <Select.Item key={page.id} value={page.label}>
                  {page.label}
                </Select.Item>
              ))}
            </Select.Group>
          </Select.Content>
        </Select.Root>
      )}
    </div>
  );
};

export const GeoAddressField: FC<FieldProps<string>> = (props) => {
  const { currentValue, onChange, title, description, placeholder, formData, brickId } = props;
  const { updateBrickProps } = useDraftHelpers();
  const [searchResults, setSearchResults] = useState<SearchResults[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Check if the value matches one of our search results
    const matchedResult = searchResults.find((result) => result.label === value);
    if (matchedResult) {
      console.log("Matched result:", matchedResult);
      // Update your state with the coordinates
      onChange(matchedResult.label);

      console.log("address changed for brick %s", brickId);

      if (brickId) {
        // Todo: fix hardcoded location props
        updateBrickProps(brickId, {
          address: matchedResult.label,
          lat: matchedResult.lat,
          lng: matchedResult.lng,
          tooltip: "",
        });
      }

      // Create a custom event containing the result with address and coordinates
      const customEvent = new CustomEvent("geoAddressSelected", {
        detail: {
          address: matchedResult.label,
          placeId: matchedResult.placeId,
          lat: matchedResult.lat,
          lng: matchedResult.lng,
        },
      });
      // Dispatch the custom event
      window.dispatchEvent(customEvent);

      // Clear search results since user made a selection
      setSearchResults([]);
      return;
    }

    console.log("No match found for %s, continuing search...", value);

    // Otherwise, trigger search
    onAddressChangeDebounced(event);
  };

  const onAddressChangeDebounced = useDebounceCallback((event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.trim();
    if (!query) {
      setSearchResults([]);
      return;
    }

    // Abort the previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort("New search initiated");
    }

    // Create a new AbortController for this request
    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;

    searchGeolocation(event.target.value, abortControllerRef.current.signal)
      .then((results) => {
        setSearchResults(results);
      })
      .catch((error) => {
        console.error("Error fetching geolocation data:", error);
        setSearchResults([]);
      });
  }, 300);

  return (
    <div className="field field-address basis-full">
      <FieldTitle title={title} description={description} />
      <datalist id="search-results">
        {searchResults.map((result) => (
          <option key={result.placeId} value={result.label} />
        ))}
      </datalist>
      <TextField.Root
        defaultValue={currentValue}
        onChange={handleAddressChange}
        className="!mt-1"
        placeholder={placeholder}
        list="search-results"
      />
    </div>
  );
};

type SearchResults = {
  label: string;
  placeId: string;
  lat: string;
  lng: string;
};

function searchGeolocation(query: string, abortSignal: AbortSignal | null = null): Promise<SearchResults[]> {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=jsonv2&layer=address&limit=8`;
  return fetch(url, {
    signal: abortSignal,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      return data.map((item: any) => ({
        label: item.display_name,
        placeId: item.place_id,
        lat: item.lat,
        lng: item.lon,
      }));
    });
}
